/**
 * rate-limit-notice.ts — Zentrales, nicht stapelndes 429-Feedback (N1a)
 *
 * Problem (Mac-QA 2026-08-17): Free/expired-Accounts laufen ins Backend-Limit
 * (100 Req/h, api_security.rb). /hotspots/topcities, /hotspots/:id, /account/me,
 * /tags, /prices/crypto lieferten 429 — das FE zeigte NUR stille leere bzw.
 * degradierte Zustaende, kein einziger Hinweis fuer den User.
 *
 * Fix: Der API-Client meldet jeden 429 hierher. Ein Cooldown sorgt dafuer, dass
 * bei N parallelen 429-Responses GENAU EINE Meldung erscheint (kein Toast-Stapel).
 * Es wird bewusst KEIN Auto-Retry und KEIN Polling gestartet — jeder weitere
 * Request wuerde den Stundenzaehler nur weiter belasten.
 */
import i18n from '../i18n'
import { toast } from '../components/Toast/useToast'

/** Innerhalb dieses Fensters wird der Hinweis nicht erneut angezeigt. */
export const RATE_LIMIT_NOTICE_COOLDOWN_MS = 60_000

const TOAST_DURATION_MS = 8000

let lastNotifiedAt = 0

/** Nur fuer Tests: Cooldown zuruecksetzen. */
export function resetRateLimitNotice(): void {
  lastNotifiedAt = 0
}

function formatWait(seconds: number): string {
  if (seconds >= 90) {
    const minutes = Math.ceil(seconds / 60)
    return i18n.t('rateLimit.waitMinutes', { count: minutes, ns: 'common' })
  }
  return i18n.t('rateLimit.waitSeconds', { count: Math.max(1, seconds), ns: 'common' })
}

/**
 * Zeigt einmalig (pro Cooldown-Fenster) einen globalen Hinweis auf das
 * erreichte Rate-Limit an.
 *
 * @param retryAfterSeconds Wartezeit aus dem Retry-After-Header, falls vorhanden
 * @returns true, wenn tatsaechlich eine Meldung ausgeloest wurde
 */
export function notifyRateLimited(retryAfterSeconds?: number | null): boolean {
  const now = Date.now()
  if (now - lastNotifiedAt < RATE_LIMIT_NOTICE_COOLDOWN_MS) return false
  lastNotifiedAt = now

  const base = i18n.t('rateLimit.message', { ns: 'common' })
  const message =
    typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0
      ? `${base} ${formatWait(retryAfterSeconds)}`
      : base

  toast.warning(message, { duration: TOAST_DURATION_MS })
  return true
}
